import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PLATFORM_LABELS } from "@/lib/helpers";
import { CheckCircle, X, ArrowsClockwise, Trash, ShieldCheck, WarningCircle, Plugs, Key } from "@phosphor-icons/react";

const STATUS_LABEL = {
  NOT_CONFIGURED: { text: "NOT CONNECTED", pill: "pill-neutral" },
  NOT_CONNECTED: { text: "NOT CONNECTED", pill: "pill-neutral" },
  READY: { text: "READY", pill: "pill-draft" },
  CONNECTED: { text: "CONNECTED", pill: "pill-live" },
  AUTH_ERROR: { text: "AUTH ERROR", pill: "pill-missing" },
  SYNCED: { text: "SYNCED", pill: "pill-live" },
  SYNC_ERROR: { text: "SYNC ERROR", pill: "pill-missing" },
};

export default function Platforms() {
  const [statuses, setStatuses] = useState([]);
  const [conns, setConns] = useState([]);
  const [busy, setBusy] = useState(null);
  const [flipkartOpen, setFlipkartOpen] = useState(false);
  const [meeshoOpen, setMeeshoOpen] = useState(false);
  const [flipkartForm, setFlipkartForm] = useState({ app_id: "", app_secret: "", seller_id: "" });
  const [meeshoForm, setMeeshoForm] = useState({ api_key: "", api_base_url: "" });

  const load = () => Promise.all([
    api.get("/marketplaces/status").then(r => setStatuses(r.data.items || [])).catch(()=>{}),
    api.get("/integrations/connections").then(r => setConns(r.data.items || [])).catch(()=>{}),
  ]);
  useEffect(() => { load(); }, []);

  const findConn = (p) => conns.find(c => c.platform === p);

  const verify = async (platform) => {
    setBusy(`verify-${platform}`);
    try {
      const r = await api.post(`/marketplaces/${platform}/verify`);
      const s = r.data.status;
      if (s === "CONNECTED") toast.success(`${PLATFORM_LABELS[platform]} verified`);
      else if (s === "AUTH_ERROR") toast.error(`${PLATFORM_LABELS[platform]}: authentication failed`);
      else toast(`${PLATFORM_LABELS[platform]}: not connected yet`);
      load();
    } catch { toast.error("Verify failed"); }
    finally { setBusy(null); }
  };

  const sync = async (platform) => {
    setBusy(`sync-${platform}`);
    try {
      const r = await api.post(`/marketplaces/${platform}/sync`);
      if (r.data.status === "RUNNING" && r.data.job_id) {
        toast(`${PLATFORM_LABELS[platform]} sync started — this can take a few minutes.`);
        pollJob(platform, r.data.job_id);
      } else {
        toast.success(`Synced · imported ${r.data.imported} · updated ${r.data.updated}`);
      }
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && (d.reason === "NOT_CONFIGURED" || d.reason === "NOT_CONNECTED")) {
        toast.error(`${PLATFORM_LABELS[platform]} is not connected yet`);
      } else if (typeof d === "string") toast.error(d);
      else toast.error(d?.message || "Sync failed");
    } finally { setBusy(null); }
  };

  const pollJob = async (platform, jobId) => {
    setBusy(`sync-${platform}`);
    const deadline = Date.now() + 15 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 5000));
      try {
        const r = await api.get(`/marketplaces/sync-jobs/${jobId}`);
        const j = r.data;
        if (j.status === "SYNCED") {
          toast.success(`${PLATFORM_LABELS[platform]} synced · created ${j.items_created} · updated ${j.items_updated}`);
          load(); setBusy(null); return;
        }
        if (j.status === "SYNC_ERROR") {
          toast.error(`${PLATFORM_LABELS[platform]} sync failed: ${j.error || j.error_code}`);
          load(); setBusy(null); return;
        }
      } catch { /* continue polling */ }
    }
    toast.error(`${PLATFORM_LABELS[platform]} sync did not finish in 15 minutes.`);
    setBusy(null); load();
  };

  const connectAmazon = async () => {
    try {
      const r = await api.get("/integrations/amazon/authorize");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amazon connect is not available yet — contact support");
    }
  };
  const connectShopify = async () => {
    const shop = prompt("Enter your Shopify shop domain (e.g. my-store.myshopify.com):");
    if (!shop) return;
    try {
      const r = await api.get(`/integrations/shopify/authorize`, { params: { shop } });
      window.location.href = r.data.authorize_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Shopify connect is not available yet"); }
  };

  const saveFlipkart = async () => {
    setBusy("save-flipkart");
    try {
      await api.post("/integrations/flipkart/setup", flipkartForm);
      toast.success("Flipkart connected");
      setFlipkartOpen(false); setFlipkartForm({ app_id: "", app_secret: "", seller_id: "" });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Flipkart rejected the credentials");
    } finally { setBusy(null); }
  };

  const saveMeesho = async () => {
    setBusy("save-meesho");
    try {
      await api.post("/integrations/meesho/setup", meeshoForm);
      toast.success("Meesho partner credentials saved");
      setMeeshoOpen(false); setMeeshoForm({ api_key: "", api_base_url: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save Meesho credentials");
    } finally { setBusy(null); }
  };

  const disconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${PLATFORM_LABELS[platform]}? Existing imported products remain in your catalog.`)) return;
    await api.delete(`/integrations/${platform}/connection`);
    toast(`${PLATFORM_LABELS[platform]} disconnected`);
    load();
  };

  const renderCard = (s) => {
    const p = s.platform;
    const conn = findConn(p);
    const isConnected = conn?.is_active && !conn?.is_demo && conn?.has_credentials;
    const lbl = STATUS_LABEL[isConnected ? (s.adapter_status === "SYNCED" ? "SYNCED" : "CONNECTED") : "NOT_CONNECTED"] || STATUS_LABEL.NOT_CONNECTED;
    return (
      <div key={p} className="card-base p-6 min-w-0" data-testid={`mkt-card-${p}`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-semibold text-navy text-lg">{PLATFORM_LABELS[p]}</div>
              <span className={`pill ${lbl.pill}`} data-testid={`mkt-status-${p}`}>{lbl.text}</span>
            </div>
            {s.last_sync && (
              <div className="text-xs font-mono-data text-muted-foreground mt-1">
                Last sync: {new Date(s.last_sync).toLocaleString()}
              </div>
            )}
            {s.error && (
              <div className="text-xs text-red-600 mt-2 flex items-start gap-1">
                <WarningCircle size={12} className="shrink-0 mt-0.5" />
                <span>{s.error}</span>
              </div>
            )}
          </div>
          {isConnected && <CheckCircle size={22} weight="fill" className="text-brand-green shrink-0" />}
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Live listings</div>
            <div className="font-mono-data font-bold text-brand-green text-lg tabular-nums">{s.listing_count || 0}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Imported</div>
            <div className="font-mono-data font-bold text-lg tabular-nums text-navy">{s.source_product_count || 0}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!isConnected ? (
            <>
              {p === "amazon" && <button data-testid={`connect-${p}`} onClick={connectAmazon} className="btn-primary py-1.5 px-3 text-[11px]"><Plugs size={12} /> Connect Amazon</button>}
              {p === "shopify" && <button data-testid={`connect-${p}`} onClick={connectShopify} className="btn-primary py-1.5 px-3 text-[11px]"><Plugs size={12} /> Connect Shopify</button>}
              {p === "flipkart" && <button data-testid={`connect-${p}`} onClick={() => setFlipkartOpen(true)} className="btn-primary py-1.5 px-3 text-[11px]"><Key size={12} /> API Setup</button>}
              {p === "meesho" && <button data-testid={`connect-${p}`} onClick={() => setMeeshoOpen(true)} className="btn-primary py-1.5 px-3 text-[11px]"><Key size={12} /> Partner Setup</button>}
            </>
          ) : (
            <>
              <button data-testid={`verify-${p}`} disabled={busy === `verify-${p}`} onClick={() => verify(p)} className="btn-secondary py-1.5 px-3 text-[11px]">
                <ShieldCheck size={12} /> {busy === `verify-${p}` ? "Verifying…" : "Verify"}
              </button>
              <button data-testid={`sync-${p}`} disabled={busy === `sync-${p}`} onClick={() => sync(p)} className="btn-primary py-1.5 px-3 text-[11px]">
                <ArrowsClockwise size={12} /> {busy === `sync-${p}` ? "Syncing…" : "Sync"}
              </button>
              <button data-testid={`disconnect-${p}`} onClick={() => disconnect(p)} className="btn-secondary py-1.5 px-3 text-[11px]" style={{ color: "hsl(var(--destructive))" }}>
                <Trash size={12} /> Disconnect
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="platforms-page" className="space-y-6 min-w-0">
      <div>
        <div className="eyebrow mb-1">Marketplace Connections</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-navy">
          Connect your marketplace accounts
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Each marketplace uses its official API. Your credentials stay encrypted and are never exposed in the browser.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 min-w-0">
        {statuses.map(renderCard)}
      </div>

      {flipkartOpen && (
        <SetupModal title="Connect Flipkart" onClose={() => setFlipkartOpen(false)}
                    onSave={saveFlipkart} saving={busy === "save-flipkart"}
                    fields={[
                      { key: "app_id", label: "Application ID", placeholder: "From Flipkart Seller Portal → API Access" },
                      { key: "app_secret", label: "Application Secret", type: "password" },
                      { key: "seller_id", label: "Seller ID (optional)" },
                    ]}
                    form={flipkartForm} setForm={setFlipkartForm}
                    testId="flipkart-setup" />
      )}
      {meeshoOpen && (
        <SetupModal title="Connect Meesho" onClose={() => setMeeshoOpen(false)}
                    onSave={saveMeesho} saving={busy === "save-meesho"}
                    fields={[
                      { key: "api_key", label: "Partner API Key", type: "password" },
                      { key: "api_base_url", label: "Partner API Base URL", placeholder: "Provided by Meesho on approval" },
                    ]}
                    form={meeshoForm} setForm={setMeeshoForm}
                    testId="meesho-setup"
                    note="Meesho API access is granted via their partner program (https://about.meesho.com)." />
      )}
    </div>
  );
}

function SetupModal({ title, onClose, onSave, saving, fields, form, setForm, testId, note }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
      <div className="bg-background border border-border rounded-xl max-w-md w-full p-8 relative shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-navy"><X size={18} /></button>
        <div className="eyebrow mb-2">Integration</div>
        <h2 className="font-display font-bold text-2xl tracking-tighter text-navy">{title}</h2>
        {note && <p className="text-xs text-muted-foreground mt-2">{note}</p>}
        <div className="mt-6 space-y-3">
          {fields.map(f => (
            <div key={f.key}>
              <label className="eyebrow block mb-1.5">{f.label}</label>
              <input type={f.type || "text"} placeholder={f.placeholder || ""}
                     data-testid={`${testId}-${f.key}`}
                     value={form[f.key]}
                     onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                     className="input-base" />
            </div>
          ))}
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary py-1.5 px-4 text-[11px]">Cancel</button>
          <button onClick={onSave} disabled={saving} data-testid={`${testId}-save`} className="btn-primary py-1.5 px-4 text-[11px]">
            {saving ? "Verifying…" : "Save & Connect"}
          </button>
        </div>
      </div>
    </div>
  );
}
